/**
 * Cart enhancements
 *
 * 1. <free-shipping-bar> — shows how far the customer is from free shipping,
 *    with a progress bar. Turns into a success message once the threshold is met.
 * 2. <cart-upsell-component> — accessory suggestions, rendered server side by
 *    `sections/cart-upsell.liquid`.
 *
 * Both are injected into the cart drawer and the cart page and keep themselves
 * in sync with the cart.
 *
 * ---------------------------------------------------------------------------
 * FREE SHIPPING SETTINGS — edit these two lines if the rules change.
 * The threshold must match Settings → Shipping and delivery.
 * ---------------------------------------------------------------------------
 */
const FREE_SHIPPING_THRESHOLD = 99900; // in minor units (øre). 99900 = 999 kr
const FREE_SHIPPING_COUNTRIES = ['DK']; // country codes that get free shipping

const CART_UPDATE_EVENT = 'cart:update';
const UPSELL_TAG = 'cart-upsell-component';
const SHIPPING_TAG = 'free-shipping-bar';

const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/* Shared cart fetch — one request per cart change, not one per component */

/** @type {Promise<any> | null} */
let cartPromise = null;

function getCart() {
  if (!cartPromise) {
    cartPromise = fetch(`${root()}cart.js`)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }
  return cartPromise;
}

document.addEventListener(CART_UPDATE_EVENT, () => {
  cartPromise = null;
});

/**
 * @param {number} minorUnits
 * @param {string} currency
 * @returns {string}
 */
function formatMoney(minorUnits, currency) {
  const amount = Math.ceil(minorUnits / 100);
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'da-DK', {
      style: 'currency',
      currency: currency || 'DKK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch (error) {
    return `${amount} kr.`;
  }
}

function eligibleForFreeShipping() {
  const country = window.Shopify && window.Shopify.country;
  if (!country) return true; // no market info — assume the primary market
  return FREE_SHIPPING_COUNTRIES.includes(country);
}

/* Free shipping progress bar */

class FreeShippingBar extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.refresh();
    document.addEventListener(CART_UPDATE_EVENT, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(CART_UPDATE_EVENT, this.#onCartUpdate);
    clearTimeout(this.#timer);
  }

  #onCartUpdate = () => {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.refresh(), 250);
  };

  async refresh() {
    if (!eligibleForFreeShipping()) return this.#clear();

    const cart = await getCart();

    if (!cart || !cart.items || cart.items.length === 0) return this.#clear();

    const total = cart.total_price || 0;
    const remaining = FREE_SHIPPING_THRESHOLD - total;
    const percent = Math.max(0, Math.min(100, (total / FREE_SHIPPING_THRESHOLD) * 100));
    const reached = remaining <= 0;

    this.dataset.reached = String(reached);
    this.innerHTML = `
      <p class="free-shipping-bar__text">
        ${
          reached
            ? '<span class="free-shipping-bar__icon" aria-hidden="true">\u{1F389}</span> Du har <strong>fri fragt</strong>'
            : `Du mangler <strong>${formatMoney(remaining, cart.currency)}</strong> for at få fri fragt`
        }
      </p>
      <div
        class="free-shipping-bar__track"
        role="progressbar"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow="${Math.round(percent)}"
      >
        <span class="free-shipping-bar__fill" style="width: ${percent}%"></span>
      </div>
    `;
    this.hidden = false;
  }

  #clear() {
    this.innerHTML = '';
    this.hidden = true;
  }
}

if (!customElements.get(SHIPPING_TAG)) {
  customElements.define(SHIPPING_TAG, FreeShippingBar);
}

/* Upsell suggestions */

class CartUpsellComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.refresh();
    document.addEventListener(CART_UPDATE_EVENT, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(CART_UPDATE_EVENT, this.#onCartUpdate);
    this.#controller?.abort();
    clearTimeout(this.#timer);
  }

  #onCartUpdate = () => {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.refresh(), 300);
  };

  async refresh() {
    const cart = await getCart();
    const productId = cart && cart.items && cart.items.length ? String(cart.items[0].product_id) : null;

    if (!productId) return this.#clear();

    const params = new URLSearchParams({
      section_id: 'cart-upsell',
      product_id: productId,
      limit: this.dataset.limit || '10',
      intent: this.dataset.intent || 'related',
    });

    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const response = await fetch(`${root()}recommendations/products?${params}`, {
        signal: this.#controller.signal,
      });

      if (!response.ok) return this.#clear();

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-cart-upsell-content]');

      if (!content || content.children.length === 0) return this.#clear();

      this.innerHTML = content.innerHTML;
      this.hidden = false;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      this.#clear();
    }
  }

  #clear() {
    this.innerHTML = '';
    this.hidden = true;
  }
}

if (!customElements.get(UPSELL_TAG)) {
  customElements.define(UPSELL_TAG, CartUpsellComponent);
}

/* Mounting */

/**
 * @param {string} tag
 * @param {string} context - 'drawer' or 'page'
 * @param {string} baseClass
 */
function createElement(tag, context, baseClass) {
  const element = document.createElement(tag);
  element.className = `${baseClass} ${baseClass}--${context}`;
  element.hidden = true;
  return element;
}

/**
 * @param {Element | null} container
 * @param {Element | null} before - insert before this element, or append when null
 * @param {string} tag
 * @param {string} context
 * @param {string} baseClass
 */
function place(container, before, tag, context, baseClass) {
  if (!container || container.querySelector(tag)) return;

  const element = createElement(tag, context, baseClass);

  if (before && before.parentElement === container) {
    container.insertBefore(element, before);
  } else {
    container.append(element);
  }
}

function mount() {
  const drawerContent = document.querySelector('.cart-drawer__dialog .cart-drawer__content');
  if (drawerContent) {
    place(drawerContent, drawerContent.firstElementChild, SHIPPING_TAG, 'drawer', 'free-shipping-bar');
    place(drawerContent, drawerContent.querySelector('.cart-drawer__summary'), UPSELL_TAG, 'drawer', 'cart-upsell');
  }

  const cartPage = document.querySelector('.cart-page');
  if (cartPage) {
    place(cartPage, cartPage.querySelector('.cart-page__items'), SHIPPING_TAG, 'page', 'free-shipping-bar');
    place(cartPage, cartPage.querySelector('.cart-page__more-blocks'), UPSELL_TAG, 'page', 'cart-upsell');
  }
}

let mountTimer;
const scheduleMount = () => {
  clearTimeout(mountTimer);
  mountTimer = setTimeout(mount, 100);
};

function init() {
  mount();

  document.addEventListener(CART_UPDATE_EVENT, scheduleMount);

  const observer = new MutationObserver(scheduleMount);

  for (const selector of ['.cart-drawer__inner', '.cart-page']) {
    const target = document.querySelector(selector);
    if (target) observer.observe(target, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
